/* eslint-disable max-lines */
import ErrorBoundary from 'components/ErrorBoundary';
import MultiTabs from 'components/Tabs/MultiTabs';
import { DocumentFieldsSectionField } from 'models/response';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import { actionType, moduleProgramCode } from 'utils/constants';

import InputQ1 from './inputQ1';
import List from './List';
import utils from 'utils';
import { RootState } from 'models';
import Checkbox from 'components/Buttons/CheckboxButton';
import Formula from './Formula';
import Multi from 'components/HOC/multi';
import ListDialog from './ListDialog';

const TabContainer = styled.div`
  display: grid;
  align-content: center;
  justify-content: center;
  padding: 0 20px;
  margin-right: -10vw;
`;

const InputFieldsContainer = styled.div`
  margin: 8px 0;
  padding-right: 222px;
  padding-bottom: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FilterQ1Positioner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FilterSpacer = styled.div`
  margin-right: 15px;
  width: 100px;
  text-align: right;
  overflow: hidden;
`;

const FilterCheckboxAndLabel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 190px;
  margin-left: 5px;
`;

const SwitchLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizePalette.medium};
  font-weight: ${({ theme }) => theme.fontWeightPalette.light};
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  padding-left: 11px;
  align-self: center;
`;

interface Props {
  isReadOnly?: boolean;
}

const Q1 = (props: Props) => {
  const documentFields = useSelector(selectors.documents.selectDocumentFields);
  const activeDocumentID = useSelector(selectors.documents.selectActiveDocumentID);
  const idProgram = useSelector(selectors.app.getProgramCode) || -1;

  const { isReadOnly } = props;
  const { val: initialValue } = useSelector((store: RootState) =>
    selectors.app.getConfig(store, actionType.DEFAULT_HIDE_FIELD_BY_CONFIDENCE),
  );
  const [hidden, setHidden] = useState(Boolean(initialValue));
  const t = utils.intl.useTranslator();
  const config = useSelector((store: RootState) => selectors.app.getConfig(store, 'FIELDS_CONFIDENCE_THRESHOLD'));
  const threshold = Number(config?.val || 0.9);
  const userPreference = useSelector(selectors.user.getUserPreference);
  const isArchived = useSelector(selectors.documents.selectIsTemplateArchived);

  const flag = useRef(false);

  useEffect(() => {
    flag.current = true; // set flag to true after first render
  }, []);

  const isContractProgram =
    idProgram === moduleProgramCode.contractArchive || idProgram === moduleProgramCode.contractValidation;

  const renderTabsContent = (sectionField: DocumentFieldsSectionField[]) => (
    <ErrorBoundary>
      <TabContainer>
        <InputFieldsContainer>
          <FilterQ1Positioner>
            <FilterSpacer />
            {!isArchived && !isContractProgram && (
              <FilterCheckboxAndLabel>
                <Checkbox name="Show only fields to verify" isActive={hidden} action={() => setHidden(!hidden)} />
                <SwitchLabel>{t('show-verified-fields')}</SwitchLabel>
              </FilterCheckboxAndLabel>
            )}
          </FilterQ1Positioner>
        </InputFieldsContainer>
        {sectionField.map(
          ({
            fieldType,
            fieldName,
            fixedName,
            translate,
            idFieldGroup,
            fieldGroup,
            fieldInputType,
            values,
            idField,
            multi = 1,
            hTableValues,
            obbl,
            formula,
            triggerConfig,
            key: optionFormat,
            dataLink,
          }) => {
            const props = {
              hasQ3: Boolean(idFieldGroup),
              label: translate,
              name: fieldName,
              fixedName,
              key: fieldName,
              fieldInputType,
              idField,
              fieldGroup,
              formula,
              protocol: activeDocumentID,
              dateFormat: userPreference.dateFormat,
              separator: userPreference.decimalSep,
              val: values,
              triggerConfig,
              dataLink,
              optionFormat,
            };

            const confidence = values[0]?.confidence || 0;
            const fieldValue = values[0]?.content || '';

            const isVisible = utils.input.isFieldVisible(hidden, confidence, threshold, obbl, fieldValue);
            if (!isVisible) return null;
            switch (fieldType) {
              case 0: {
                return multi ? (
                  <Multi
                    InputComponent={(propsComponent: any) => (
                      <InputQ1 readonly={isReadOnly} multi {...props} {...propsComponent} />
                    )}
                    idField={idField}
                    name={fieldName}
                    label={translate}
                  />
                ) : (
                  <InputQ1 readonly={isReadOnly} {...props} />
                );
              }
              case 1:
                return <Formula {...props} readonly={isReadOnly} flag={flag} />;
              case 3:
                return multi ? (
                  <Multi
                    InputComponent={(propsComponent: any) => (
                      <List
                        readonly={isReadOnly}
                        multi
                        {...props}
                        {...propsComponent}
                        options={utils.input.buildOptions(
                          hTableValues,
                          utils.input.getLabelTypeByKey(optionFormat).includes('description') ? 'description' : 'code',
                          'code',
                          utils.input.getLabelTypeByKey(optionFormat) === 'code-description',
                        )}
                      />
                    )}
                    idField={idField}
                    name={fieldName}
                    label={translate}
                  />
                ) : (
                  <List
                    readonly={isReadOnly}
                    {...props}
                    options={utils.input.buildOptions(
                      hTableValues,
                      utils.input.getLabelTypeByKey(optionFormat).includes('description') ? 'description' : 'code',
                      'code',
                      utils.input.getLabelTypeByKey(optionFormat) === 'code-description',
                    )}
                  />
                );
              case 7:
                return <p>combo-list-7</p>;
              case 8:
              case 10:
                return multi ? (
                  <Multi
                    InputComponent={(propsComponent: any) => (
                      <ListDialog
                        readonly={isReadOnly}
                        multi
                        {...props}
                        {...propsComponent}
                        hTableValues={hTableValues}
                        searchBycode={!utils.input.getLabelTypeByKey(optionFormat).includes('description')}
                        dialogTextInput={fieldType === 10}
                      />
                    )}
                    idField={idField}
                    name={fieldName}
                    label={translate}
                  />
                ) : (
                  <ListDialog
                    readonly={isReadOnly}
                    {...props}
                    hTableValues={hTableValues}
                    searchBycode={!utils.input.getLabelTypeByKey(optionFormat).includes('description')}
                    dialogTextInput={fieldType === 10}
                  />
                );
              case 21:
                return multi ? (
                  <Multi
                    InputComponent={(propsComponent: any) => <InputQ1 readonly multi {...props} {...propsComponent} />}
                    idField={idField}
                    name={fieldName}
                    label={translate}
                    readonly
                  />
                ) : (
                  <InputQ1 readonly {...props} />
                );
              default:
                return <p>{fieldType} not supported</p>;
            }
          },
        )}
      </TabContainer>
    </ErrorBoundary>
  );
  const isExpanded = useSelector(selectors.app.getHeaderExpanded);
  // dynamic tabs and tab's content from documentFields response
  const tabs = documentFields?.map((elm, i) => ({
    name: elm.description,
    id: i,
    content: renderTabsContent(elm.sectionFields.filter((filtered) => filtered.position > 0)),
  }));
  //
  return tabs?.length ? (
    <MultiTabs maxHeight={isExpanded ? 'calc(100vh - 388px)' : 'calc(100vh - 234px)'} tabs={tabs} />
  ) : null;
};

export default Q1;
